import { useAuth } from '@clerk/react';
import type { CompleteOnboardingDto } from '@shared/dto/onboarding/complete-onboarding.dto';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useTranslation } from 'react-i18next';
import { OnboardingService } from '../services/onboarding/onboarding.service';
import type { IOnboardingService } from '../services/onboarding/onboarding.service.interface';
import { getHttpErrorStatus } from '../utils/error.utils';
import { useValidatedTenantId } from './auth/use-validated-tenant-id';
import { useNotification } from './use-notification';

const HTTP_CONFLICT = 409;

const ONBOARDING_STATUS_ROOT = ['onboarding', 'status'] as const;

export const onboardingQueryKeys = {
  statusRoot: () => ONBOARDING_STATUS_ROOT,
  status: (tenantId: string | null | undefined) => [...ONBOARDING_STATUS_ROOT, tenantId] as const,
};

export function useOnboardingStatus() {
  const service = useInjection<IOnboardingService>(OnboardingService);
  const { isSignedIn } = useAuth();
  const currentTenantId = useValidatedTenantId();

  return useQuery({
    queryKey: onboardingQueryKeys.status(currentTenantId),
    queryFn: () => service.getStatus(),
    staleTime: 5 * 60 * 1000,
    enabled: !!isSignedIn && !!currentTenantId,
    retry: 1,
  });
}

export function useCompleteOnboarding() {
  const service = useInjection<IOnboardingService>(OnboardingService);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { showError } = useNotification();

  // No onSuccess: the page plays the completion screen first and only then
  // refreshes the status, which is what redirects home.
  return useMutation({
    mutationFn: (data: CompleteOnboardingDto) => service.complete(data),
    onError: (err) => {
      // 409 = onboarding already completed from another tab/device: refresh the
      // status so the page gate redirects home instead of trapping the user.
      if (getHttpErrorStatus(err) === HTTP_CONFLICT) {
        void queryClient.invalidateQueries({ queryKey: onboardingQueryKeys.statusRoot() });
        return;
      }
      console.error('useCompleteOnboarding error:', err);
      showError(t('onboarding.error'));
    },
  });
}
